import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import UserAdditionalInfoModal from "../../components/UserAdditionalInfoModal";
import AdditionalInfoModal from "../../components/AdditionalInfoModal";
import { BACKEND_URL } from "../../utils/config.js";
import { handleFetchResponse } from "./helpers.js";
import "./ViewAllUsers.css";

const FRONTEND_URL = process.env.REACT_APP_FRONTEND_URL || "/backend";

export default function ViewAllUsers() {
  const [users, setUsers] = useState([]);
  const [psws, setPsws] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [filteredPsws, setFilteredPsws] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [pswSearch, setPswSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showUserAdditionalInfo, setShowUserAdditionalInfo] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [showPswAdditionalInfo, setShowPswAdditionalInfo] = useState(false);
  const [selectedPswId, setSelectedPswId] = useState(null);
  const [collapsedSections, setCollapsedSections] = useState({ users: false, psws: false });
  const navigate = useNavigate();

  useEffect(() => {
    const fetchAllUsers = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch users
        const usersRes = await fetch(`${BACKEND_URL}/admin/users`, {
          credentials: 'include'
        });
        const checkedUsersRes = await handleFetchResponse(usersRes, navigate);
        if (!checkedUsersRes) return;

        if (!checkedUsersRes.ok) {
          throw new Error("Failed to fetch users");
        }
        const usersData = await checkedUsersRes.json();
        const usersList = Array.isArray(usersData) ? usersData : usersData.users || [];

        // Fetch PSWs
        const pswsRes = await fetch(`${BACKEND_URL}/admin/psws`, {
          credentials: 'include'
        });
        const checkedPswsRes = await handleFetchResponse(pswsRes, navigate);
        if (!checkedPswsRes) return;

        if (!checkedPswsRes.ok) {
          throw new Error("Failed to fetch PSWs");
        }
        const pswsData = await checkedPswsRes.json();
        const pswsList = Array.isArray(pswsData) ? pswsData : pswsData.users || [];
        
        setUsers(usersList);
        setPsws(pswsList);
        setFilteredUsers(usersList);
        setFilteredPsws(pswsList);
      } catch (err) {
        setError(err?.message || "Failed to load users and PSWs");
        console.error("Error fetching users:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAllUsers();
  }, [BACKEND_URL]);

  // Filter users based on search
  useEffect(() => {
    const searchLower = userSearch.toLowerCase();
    const filtered = users.filter(user => {
      const name = `${user.first_name || ""} ${user.last_name || ""}`.toLowerCase();
      const email = (user.email || "").toLowerCase();
      const username = (user.username || "").toLowerCase();
      
      return (
        name.includes(searchLower) ||
        email.includes(searchLower) ||
        username.includes(searchLower)
      );
    });
    setFilteredUsers(filtered);
  }, [userSearch, users]);

  // Filter PSWs based on search
  useEffect(() => {
    const searchLower = pswSearch.toLowerCase();
    const filtered = psws.filter(psw => {
      const name = `${psw.first_name || ""} ${psw.last_name || ""}`.toLowerCase();
      const email = (psw.email || "").toLowerCase();
      const username = (psw.username || "").toLowerCase();
      
      return (
        name.includes(searchLower) ||
        email.includes(searchLower) ||
        username.includes(searchLower)
      );
    });
    setFilteredPsws(filtered);
  }, [pswSearch, psws]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleViewUserAdditionalInfo = (userId) => {
    setSelectedUserId(userId);
    setShowUserAdditionalInfo(true);
  };

  const handleCloseUserAdditionalInfo = () => {
    setShowUserAdditionalInfo(false);
    setSelectedUserId(null);
  };

  const handleViewPswAdditionalInfo = (pswId) => {
    setSelectedPswId(pswId);
    setShowPswAdditionalInfo(true);
  };

  const handleClosePswAdditionalInfo = () => {
    setShowPswAdditionalInfo(false);
    setSelectedPswId(null);
  };

  const toggleSection = (section) => {
    setCollapsedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleDeleteUser = async (userId) => {
    if (!userId) {
      console.error('No user ID provided');
      return;
    }

    const user = users.find(u => u.patient_id === userId || u.user_id === userId || u.id === userId);
    const userName = user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : 'this user';

    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${userName}? This action cannot be undone.`
    );

    if (!confirmDelete) {
      return;
    }

    try {
      const res = await fetch(`${BACKEND_URL}/admin/user/${userId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const checkedRes = await handleFetchResponse(res, navigate);
      if (!checkedRes) return;

      if (!checkedRes.ok) {
        throw new Error(`Failed to delete user: ${checkedRes.status}`);
      }

      // Remove user from state
      setUsers(prevUsers => 
        prevUsers.filter(u => u.patient_id !== userId && u.user_id !== userId && u.id !== userId)
      );

      alert(`${userName} deleted successfully!`);
    } catch (err) {
      console.error('Error deleting user:', err);
      alert(`Failed to delete user: ${err.message}`);
    }
  };

  const handleDeletePsw = async (pswId) => {
    if (!pswId) {
      console.error('No PSW ID provided');
      return;
    }

    const psw = psws.find(p => p.psw_id === pswId || p.user_id === pswId || p.id === pswId);
    const pswName = psw ? `${psw.first_name || ''} ${psw.last_name || ''}`.trim() : 'this service worker';

    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${pswName}? This action cannot be undone.`
    );

    if (!confirmDelete) {
      return;
    }

    try {
      const res = await fetch(`${BACKEND_URL}/admin/psws/${pswId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const checkedRes = await handleFetchResponse(res, navigate);
      if (!checkedRes) return;

      if (!checkedRes.ok) {
        throw new Error(`Failed to delete service worker: ${checkedRes.status}`);
      }

      // Remove PSW from state
      setPsws(prevPsws => 
        prevPsws.filter(p => p.psw_id !== pswId && p.user_id !== pswId && p.id !== pswId)
      );

      alert(`${pswName} deleted successfully!`);
    } catch (err) {
      console.error('Error deleting PSW:', err);
      alert(`Failed to delete service worker: ${err.message}`);
    }
  };

  if (loading) {
    return (
      <div className="view-users-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p className="loading-message">Loading users and PSWs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="view-users-page">
      <div className="view-users-header">
        <button className="back-button" onClick={handleBack} title="Go back">
          ← Back
        </button>
        <h1>Users & Service Workers</h1>
      </div>

      {error && (
        <div className="error-message">
          <p>{error}</p>
        </div>
      )}

      <div className="users-content">
        {/* Users Table Section */}
        <section className="users-section">
          <div className="section-header" onClick={() => toggleSection('users')} style={{ cursor: 'pointer' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '14px' }}>{collapsedSections.users ? '▶' : '▼'}</span>
              <h2>Users ({filteredUsers.length} of {users.length})</h2>
            </div>
            {!collapsedSections.users && (
            <div className="search-box">
              <input
                type="text"
                placeholder="Search users by name, email, or username..."
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                className="search-input"
              />
              {userSearch && (
                <button
                  className="clear-search"
                  onClick={() => setUserSearch("")}
                  title="Clear search"
                >
                  ✕
                </button>
              )}
            </div>
            )}
          </div>

          {!collapsedSections.users && (
          <div className="table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Username</th>
                  <th>Phone</th>
                  <th>Date of Birth</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length > 0 ? (
                  filteredUsers.map((user) => (
                    <tr key={user.patient_id || user.user_id || user.id}>
                      <td>
                        {`${user.first_name || ""} ${user.last_name || ""}`.trim() ||
                          "N/A"}
                      </td>
                      <td>{user.email || "N/A"}</td>
                      <td>{user.username || "N/A"}</td>
                      <td>{user.phone_number || "N/A"}</td>
                      <td>{user.date_of_birth || "N/A"}</td>
                      <td>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            className="action-button"
                            onClick={() => handleViewUserAdditionalInfo(user.patient_id || user.user_id || user.id)}
                            title="View additional information"
                          >
                            View Info
                          </button>
                          <button
                            className="action-button delete-button"
                            onClick={() => handleDeleteUser(user.patient_id || user.user_id || user.id)}
                            title="Delete user"
                            style={{ backgroundColor: '#dc2626', color: 'white' }}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="empty-cell">
                      <div className="empty-state">
                        <span className="empty-icon">👥</span>
                        <p>
                          {userSearch
                            ? "No users found matching your search"
                            : "No users available"}
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          )}
        </section>

        {/* PSWs Table Section */}
        <section className="users-section">
          <div className="section-header" onClick={() => toggleSection('psws')} style={{ cursor: 'pointer' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '14px' }}>{collapsedSections.psws ? '▶' : '▼'}</span>
              <h2>Service Workers (PSWs) ({filteredPsws.length} of {psws.length})</h2>
            </div>
            {!collapsedSections.psws && (
            <div className="search-box">
              <input
                type="text"
                placeholder="Search PSWs by name, email, or username..."
                value={pswSearch}
                onChange={(e) => setPswSearch(e.target.value)}
                className="search-input"
              />
              {pswSearch && (
                <button
                  className="clear-search"
                  onClick={() => setPswSearch("")}
                  title="Clear search"
                >
                  ✕
                </button>
              )}
            </div>
            )}
          </div>

          {!collapsedSections.psws && (
          <div className="table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Username</th>
                  <th>Phone</th>
                  <th>Date of Birth</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPsws.length > 0 ? (
                  filteredPsws.map((psw) => (
                    <tr key={psw.psw_id || psw.user_id || psw.id}>
                      <td>
                        {`${psw.first_name || ""} ${psw.last_name || ""}`.trim() ||
                          "N/A"}
                      </td>
                      <td>{psw.email || "N/A"}</td>
                      <td>{psw.username || "N/A"}</td>
                      <td>{psw.phone_number || "N/A"}</td>
                      <td>{psw.date_of_birth || "N/A"}</td>
                      <td>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            className="action-button"
                            onClick={() => handleViewPswAdditionalInfo(psw.psw_id || psw.user_id || psw.id)}
                            title="View additional information"
                          >
                            View Info
                          </button>
                          <button
                            className="action-button delete-button"
                            onClick={() => handleDeletePsw(psw.psw_id || psw.user_id || psw.id)}
                            title="Delete service worker"
                            style={{ backgroundColor: '#dc2626', color: 'white' }}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="empty-cell">
                      <div className="empty-state">
                        <span className="empty-icon">🔧</span>
                        <p>
                          {pswSearch
                            ? "No PSWs found matching your search"
                            : "No PSWs available"}
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          )}
        </section>
      </div>

      {/* User Additional Information Modal */}
      <UserAdditionalInfoModal
        isOpen={showUserAdditionalInfo}
        onClose={handleCloseUserAdditionalInfo}
        userId={selectedUserId}
        isAdminView={true}
      />

      {/* PSW Additional Information Modal */}
      <AdditionalInfoModal
        isOpen={showPswAdditionalInfo}
        onClose={handleClosePswAdditionalInfo}
        pswId={selectedPswId}
        isAdminView={true}
      />
    </div>
  );
}
